/// <amd-module name="TFS/Admin/Groups/Pivots/ServicePrincipals/Components/ServicePrincipalsPivotInitializer" />
import * as React from "react";
import { connect } from "react-redux";
import { PersonaDetailsProvider } from "TFS/Admin/Common/Providers/PersonaDetailsProvider";
import { FilterablePivot, IFilterablePivotOwnProps, IFilterablePivotProps } from "TFS/Admin/Groups/Common/Components/FilterablePivot";
import * as Constants from "TFS/Admin/Groups/Common/Constants";
import { CustomGraphServicePrincipal, IOrganizationGroupsAwareState } from "TFS/Admin/Groups/Common/Contracts";
import { GroupsViewActions } from "TFS/Admin/Groups/Common/Redux/Actions/GroupsViewActions";
import * as PageSelectors from "TFS/Admin/Groups/Common/Redux/GroupsViewSelectors";
import * as Resources from "TFS/Admin/Groups/Common/Resources";
import { convertGraphMemberToIIdentity } from "TFS/Admin/Groups/Common/Util/CommonTypeConverters";
import { GroupsComponent } from "TFS/Admin/Groups/Common/Util/GroupsTelemetryLogger";
import { ServicePrincipalsPivotActions } from "TFS/Admin/Groups/Pivots/ServicePrincipals/Redux/ServicePrincipalsPivotActions";
import * as ServicePrincipalSelectors from "TFS/Admin/Groups/Pivots/ServicePrincipals/Redux/ServicePrincipalsPivotSelectors";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { Card } from "VSSUI/Card";
import { CustomHeader, HeaderTitleArea } from "VSSUI/Header";
import { IIdentity } from "VSSUI/IdentityPicker";
import { ListSelection } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { Pill, PillSize } from "VSSUI/Pill";
import { PillGroup } from "VSSUI/PillGroup";
import { ITableColumn, ITableRow, ITableRowDetails, Table, TableColumnStyle, TableLoadingRow, TableRow, TwoLineTableCell } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";

import { announce } from "VSS/Core/Util/Accessibility";
import { format } from "VSS/Core/Util/String";

export interface IServicePrincipalsPivotContainerProps extends IFilterablePivotProps {}

export interface IServicePrincipalsPivotInitializerOwnProps extends IServicePrincipalsPivotContainerProps, IFilterablePivotOwnProps {
    loading: boolean;
    identities: CustomGraphServicePrincipal[];
    moreData: boolean;
    totalIdentities: number;
    continutationToken?: string[];
    initComplete: boolean;
    selectedServicePrincipal: CustomGraphServicePrincipal;
    selectedIdentity: IIdentity;
    messageCardText?: string;
    messageCardSeverity?: MessageCardSeverity;
    scopeDescriptor: string | undefined;
    permissionsPage: Constants.IPermissionPage;
    isCollectionNavigationAllowed: boolean;
}

export type IServicePrincipalsPivotInitializerProps = IServicePrincipalsPivotInitializerOwnProps & typeof ActionsToDispatch;

export class ServicePrincipalsPivotInitializer extends FilterablePivot<IServicePrincipalsPivotInitializerProps> {
    private _itemProvider: IObservableArray<CustomGraphServicePrincipal>;
    private _selection = new ListSelection(false);
    private _personaDetailsProvider: PersonaDetailsProvider;

    constructor(props: IServicePrincipalsPivotInitializerProps) {
        super(props);

        this.props.setFilterPlaceholder(Resources.SearchServicePrincipalByPrefix);
        this.props.initView(this.props.scopeDescriptor, this.props.permissionsPage);

        const servicePrincipalRows: any[] = this.props.identities.slice();
        if (this.props.moreData && this.props.identities.length) {
            servicePrincipalRows.push(new ObservableValue<any | undefined>(undefined));
        }
        this._itemProvider = new ObservableArray<any>(servicePrincipalRows);
        this._personaDetailsProvider = new PersonaDetailsProvider(this.props.pageContext);
    }

    public componentDidUpdate(prevProps: IServicePrincipalsPivotInitializerProps) {
        const servicePrincipalRows: any[] = this.props.identities.slice();

        if (this.props.moreData && this.props.identities.length) {
            servicePrincipalRows.push(new ObservableValue<any | undefined>(undefined));
        }

        this._itemProvider.splice(0, this._itemProvider.length, ...servicePrincipalRows);

        // Narrator not built in FilterablePivot because of the multi-render issue
        if (prevProps.searchResult && prevProps.searchResult.status != Constants.SearchStatus.Completed && this._isSearchCompleted()) {
            announce(format(Resources.SearchResultsCount, this.props.searchResult.resultItems.length));
        }
    }

    private _isSearchCompleted() {
        return this.props.searchResult.status == Constants.SearchStatus.Completed;
    }

    /***Filterable Pivot Methods */
    protected _getItemsName(): string {
        return Resources.ServicePrincipals;
    }

    protected _isProjectScope(): boolean {
        return this.props.scopeDescriptor ? true : false;
    }

    protected _isCollectionNavigationAllowed(): boolean {
        return this.props.isCollectionNavigationAllowed;
    }

    protected _onExpandSearch(): void {
        this.props.logTelemetry(GroupsComponent.ServicePrincipalsPivot, "Expanded search", {});
        this.props.searchServicePrincipals(this.props.searchResult.keyword, "", "", true, Constants.IPermissionPage.Project);
    }

    protected _isContentReady(): boolean {
        return !this.props.loading && !this._isFiltering();
    }

    protected _getFilteredContentColumns(): ITableColumn<CustomGraphServicePrincipal>[] {
        return this._getColumns();
    }

    protected _onRenderRow(index: number, item: CustomGraphServicePrincipal, details: ITableRowDetails<CustomGraphServicePrincipal>): JSX.Element {
        return <TableRow key={index} index={index} details={details} />;
    }

    protected _renderContent(): JSX.Element {
        return this._getRenderElements(this._itemProvider, this._getColumns(), false);
    }

    protected _renderFilteredContent(): JSX.Element {
        const { resultItems } = this.props.searchResult;
        return resultItems.length > 0
            ? this._getRenderElements(new ObservableArray<any>(resultItems), this._getFilteredContentColumns(), true)
            : this._getZeroData();
    }

    protected _onFilterChanged(keyword: string): void {
        this.props.logTelemetry(GroupsComponent.ServicePrincipalsPivot, "Filter changed", {});
        this.props.searchServicePrincipals(
            keyword,
            this.props.searchResult ? this.props.searchResult.keyword : "",
            this.props.scopeDescriptor,
            false,
            this.props.permissionsPage
        );
    }

    protected _componentDidMount() {
        this.props.logTelemetry(GroupsComponent.ServicePrincipalsPivot, "Pivot loaded", {});
    }
    /***End Filterable Pivot methods */

    private _getRenderElements(
        itemProvider: IObservableArray<CustomGraphServicePrincipal>,
        columns: ITableColumn<CustomGraphServicePrincipal>[],
        filtered: boolean
    ): JSX.Element {
        return (
            <div>
                {this.props.messageCardText ? (
                    <MessageCard
                        className="permissions-card"
                        severity={this.props.messageCardSeverity}
                        onDismiss={() => this.props.onDismissMessageCard()}
                    >
                        {this.props.messageCardText}
                    </MessageCard>
                ) : null}
                <Card className="flex-grow bolt-card-no-vertical-padding" contentProps={{ contentPadding: false }}>
                    <div className="flex-column">
                        <CustomHeader className="bolt-header-with-commandbar no-bottom-padding">
                            <HeaderTitleArea className="flex-row table-title-text">
                                <PillGroup>
                                    <span className="text-ellipsis">{Resources.Total}</span>
                                    <Pill containsCount={true} excludeTabStop={true} size={PillSize.compact}>
                                        {this.props.identities.length + (this.props.moreData ? "+" : "")}
                                    </Pill>
                                    {this.props.searchResult && this._isSearchCompleted() && (
                                        <>
                                            <span className="text-ellipsis">{Resources.Filtered}</span>
                                            <Pill containsCount={true} excludeTabStop={true} size={PillSize.compact}>
                                                {this.props.searchResult.resultItems.length}
                                            </Pill>
                                        </>
                                    )}
                                </PillGroup>
                            </HeaderTitleArea>
                        </CustomHeader>
                        <Table
                            itemProvider={itemProvider}
                            columns={columns}
                            onActivate={this._onRowClicked}
                            selection={this._selection}
                            singleClickActivation={true}
                            renderLoadingRow={this._renderLoadRow}
                        />
                    </div>
                </Card>
            </div>
        );
    }

    private _onRowClicked = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<CustomGraphServicePrincipal>) => {
        this.props.logTelemetry(GroupsComponent.ServicePrincipalsPivot, "Open service principal detail view", {});
        const { ctrlKey, metaKey, shiftKey, button } = (event || {}) as React.MouseEvent<HTMLElement>;

        if (!ctrlKey && !metaKey && !shiftKey && button !== 1) {
            this.props.selectedServicePrincipalChanged(tableRow.data);

            // Prevent the default navigation from taking place.
            if (event) {
                event.preventDefault();
            }
        }
    };

    private _getColumns(): ITableColumn<CustomGraphServicePrincipal>[] {
        return [
            {
                id: "Name",
                name: Resources.Name,
                renderCell: this._renderNameColumn,
                width: new ObservableValue(-100),
                columnStyle: TableColumnStyle.Primary,
                onSize: this._onSizeColumn
            }
        ];
    }

    private _renderNameColumn = (
        rowIndex: number,
        columnIndex: number,
        tableColumn: ITableColumn<CustomGraphServicePrincipal>,
        tableItem: CustomGraphServicePrincipal
    ): JSX.Element => {
        return (
            <TwoLineTableCell
                columnIndex={columnIndex}
                tableColumn={tableColumn}
                key={columnIndex}
                line1={
                    <Tooltip overflowOnly={true}>
                        <span className="fontWeightSemiBold bolt-table-two-line-cell-item text-ellipsis">{tableItem.displayName}</span>
                    </Tooltip>
                }
                line2={
                    <Tooltip overflowOnly={true}>
                        <span className="fontSizeMS bolt-table-two-line-cell-item secondary-text flex-row flex-baseline text-ellipsis">
                            {tableItem.applicationId}
                        </span>
                    </Tooltip>
                }
                iconProps={{
                    render: (className?: string | undefined): JSX.Element => this._renderImageIcon(tableItem, className)
                }}
            />
        );
    };

    private _renderImageIcon(tableItem: CustomGraphServicePrincipal, className?: string | undefined): JSX.Element {
        return (
            <Persona
                className="display-members-persona"
                size={PersonaSize.size40}
                identity={convertGraphMemberToIIdentity(this.props.pageContext, tableItem)}
                personaProvider={this._personaDetailsProvider}
            />
        );
    }

    private _onRenderMissingItem = (): null => {
        this.props.logTelemetry(GroupsComponent.ServicePrincipalsPivot, "Load more service principals on scroll", {});
        this.props.onRenderMissingItem(this.props.continutationToken, this.props.scopeDescriptor, this.props.permissionsPage);
        return null;
    };

    private _onSizeColumn(event: MouseEvent, index: number, width: number, column: ITableColumn<CustomGraphServicePrincipal>) {
        (column.width as IObservableValue<number>).value = width;
    }

    private _renderLoadRow = (index: number, details: ITableRowDetails<CustomGraphServicePrincipal>): JSX.Element => {
        this._onRenderMissingItem();

        return <TableLoadingRow columns={this._getColumns()} details={details} key={index} rowIndex={index} />;
    };
}

// Update component props whenever the store's state changes
function mapStateToProps(state: IOrganizationGroupsAwareState, providedProps: IServicePrincipalsPivotContainerProps): Partial<IServicePrincipalsPivotInitializerOwnProps> {
    return {
        ...providedProps,
        identities: ServicePrincipalSelectors.getServicePrincipals(state),
        moreData: ServicePrincipalSelectors.fetchMoreDataOnLastRow(state),
        totalIdentities: ServicePrincipalSelectors.getTotalIdentityCount(state),
        continutationToken: ServicePrincipalSelectors.getContinuationToken(state),
        initComplete: ServicePrincipalSelectors.getInitComplete(state),
        selectedServicePrincipal: PageSelectors.getSelectedServicePrincipal(state),
        messageCardText: ServicePrincipalSelectors.getMessageCardText(state),
        messageCardSeverity: ServicePrincipalSelectors.getMessageCardSeverity(state),
        loading: ServicePrincipalSelectors.showLoadingPanel(state),
        searchResult: ServicePrincipalSelectors.getSearchResult(state),

        // Page Selectors
        scopeDescriptor: PageSelectors.getScopeDescriptor(state),
        permissionsPage: PageSelectors.getPermissionPage(state),
        isCollectionNavigationAllowed: PageSelectors.isCollectionNavigationAllowed(state)
    };
}

// Hook up action creators to reducer
const ActionsToDispatch = {
    initView: ServicePrincipalsPivotActions.initView,
    disposeView: ServicePrincipalsPivotActions.disposeView,
    onRenderMissingItem: ServicePrincipalsPivotActions.onRenderMissingItem,
    onDismissMessageCard: ServicePrincipalsPivotActions.onDismissMessageCard,
    searchServicePrincipals: ServicePrincipalsPivotActions.searchServicePrincipals,

    // Group view actions
    selectedServicePrincipalChanged: GroupsViewActions.selectedServicePrincipalChanged,
    logTelemetry: GroupsViewActions.logTelemetry,
    setFilterPlaceholder: GroupsViewActions.setFilterPlaceholder
};

export const ConnectedServicePrincipalsPivotInitializer = connect(mapStateToProps, ActionsToDispatch)(ServicePrincipalsPivotInitializer);
